import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, DatePicker, Empty, Popover as AntPopover, Select, Spin, Typography, message, Tooltip as AntTooltip } from 'antd';
import { CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts';
import { getNamespaceGatewayGapsApi } from '../../../api/customerApi';
import { DECODED_ACCESS_TOKEN } from '../../../constants/system.keys';
import storageService from '../../../services/storage.service';
import {
    calculateGatewaysSLA,
    formatSecondsToHHMMSS,
    getBarColor,
} from '../../../untils/gateway';

const { RangePicker } = DatePicker;
const { Option } = Select;

const { Text } = Typography;

const CustomBarShape = (props) => {
    const { fill, x, y, width, height, payload, t } = props;
    if (!payload) return <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} ry={4} />;

    const handleCopySerial = (e) => {
        if (e) e.stopPropagation();
        if (payload.gatewaySerial) {
            navigator.clipboard.writeText(payload.gatewaySerial);
            message.success(t('Copied serial successfully') || `Copied ${payload.gatewaySerial}`);
        }
    };

    const popoverContent = (
        <div style={{ padding: '4px 6px', maxWidth: '300px' }}>
            <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#60a5fa', marginBottom: '4px' }}>
                {payload.gatewayName || payload.gatewaySerial}
            </div>
            <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span>Serial: {payload.gatewaySerial}</span>
                {payload.gatewaySerial && (
                    <AntTooltip title={t('Copy Serial')}>
                        <CopyOutlined
                            onClick={handleCopySerial}
                            style={{
                                cursor: 'pointer',
                                color: '#60a5fa',
                                fontSize: '14px',
                                padding: '2px',
                            }}
                        />
                    </AntTooltip>
                )}
            </div>
            <div style={{ fontSize: '13px', marginBottom: '2px' }}>
                {t('Upload Rate')}:{' '}
                <strong style={{ color: getBarColor(payload.uploadedPercent) }}>
                    {payload.uploadedPercent}%
                </strong>
            </div>
            <div style={{ fontSize: '12px', color: '#ccc' }}>
                {t('Online camera count')}: {payload.cameraOnline}/{payload.cameraTotal} {t('camera')}
            </div>
            <div style={{ fontSize: '12px', color: '#ccc' }}>
                {t('Gap duration (missing)')}: {payload.totalGapFormatted || `${payload.totalGapS}s`}
            </div>
        </div>
    );

    return (
        <AntPopover content={popoverContent} trigger="hover" placement="right">
            <g style={{ cursor: 'pointer' }} onClick={handleCopySerial}>
                <rect x={x} y={y} width={width} height={height} fill={fill} rx={4} ry={4} />
            </g>
        </AntPopover>
    );
};

const BarLabel = (props) => {
    const { x, y, width, height, value } = props;
    if (value === undefined || value === null) return null;
    return (
        <text
            x={x + width + 8}
            y={y + height / 2 + 4}
            fill="#e5e7eb"
            fontSize="11"
            fontWeight="600"
            textAnchor="start"
        >
            {`${value}%`}
        </text>
    );
};

const GatewaySLA = ({ gateways: propGateways }) => {
    const { t } = useTranslation();
    const { namespace } = useSelector((state) => state);
    const [gatewaysData, setGatewaysData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [dateRanger, setDateRanger] = useState([
        moment().set({ hour: 0, minute: 0, second: 0 }).valueOf(),
        moment().set({ hour: 23, minute: 59, second: 59 }).valueOf(),
    ]);

    const getSelectedOption = useCallback(() => {
        if (!dateRanger || dateRanger.length !== 2) return undefined;
        const start = moment(dateRanger[0]).startOf('day');
        const end = moment(dateRanger[1]).startOf('day');
        const diffDays = end.diff(start, 'days') + 1;

        if (diffDays === 1) return 1;
        if (diffDays === 3) return 3;
        if (diffDays === 7) return 7;
        if (diffDays >= 28 && diffDays <= 31) return 30;
        return undefined;
    }, [dateRanger]);

    const changeDateRanger = (delta) => {
        const dataFromTime = Math.floor(
            moment()
                .subtract({ days: delta - 1 })
                .set({ hour: 0, minute: 0, second: 0 })
                .valueOf(),
        );
        const dataToTime = Math.floor(
            moment().set({ hour: 23, minute: 59, second: 59 }).valueOf(),
        );
        setDateRanger([dataFromTime, dataToTime]);
    };

    const handleDateRangerChange = (dates) => {
        if (!dates || dates.length !== 2) {
            setDateRanger(null);
        } else {
            const dataFromTime = Math.floor(
                dates[0].set({ hour: 0, minute: 0, second: 0 }).valueOf(),
            );
            const dataToTime = Math.floor(
                dates[1].set({ hour: 23, minute: 59, second: 59 }).valueOf(),
            );
            setDateRanger([dataFromTime, dataToTime]);
        }
    };

    const fromDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[0]) return moment().format('YYYY-MM-DD');
        return moment(dateRanger[0]).format('YYYY-MM-DD');
    }, [dateRanger]);

    const toDateStr = useMemo(() => {
        if (!dateRanger || !dateRanger[1]) return moment().format('YYYY-MM-DD');
        return moment(dateRanger[1]).format('YYYY-MM-DD');
    }, [dateRanger]);


    const fetchGatewayGaps = useCallback(async () => {
        if (!namespace) return;
        setLoading(true);
        try {
            const token = storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);
            const dataQuery = {
                fromDate: fromDateStr,
                toDate: toDateStr,
            };
            const res = await getNamespaceGatewayGapsApi(token, { namespace }, dataQuery);
            if (res && res.data) {
                const apiData = res.data.data || res.data;
                setGatewaysData(apiData.gateways || (Array.isArray(apiData) ? apiData : []));
            } else {
                setGatewaysData([]);
            }
        } catch (err) {
            console.error('Error fetching gateway gaps:', err);
            setGatewaysData([]);
        } finally {
            setLoading(false);
        }
    }, [namespace, fromDateStr, toDateStr]);

    useEffect(() => {
        fetchGatewayGaps();
    }, [fetchGatewayGaps]);

    const effectiveGateways = propGateways && propGateways.length > 0 ? propGateways : gatewaysData;

    const allCalculatedGateways = useMemo(() => {
        return calculateGatewaysSLA(effectiveGateways, dateRanger);
    }, [effectiveGateways, dateRanger]);

    const top20LowestGateways = useMemo(() => {
        return allCalculatedGateways.slice(0, 20);
    }, [allCalculatedGateways]);

    const handleExportExcel = useCallback(() => {
        if (!allCalculatedGateways || allCalculatedGateways.length === 0) {
            message.warning(t('No data available to export') || 'No data available to export');
            return;
        }

        try {
            const exportData = allCalculatedGateways.map((gw, index) => ({
                'STT': index + 1,
                [t('Gateway Name') || 'Tên Gateway']: gw.gatewayName || gw.gatewaySerial || 'N/A',
                [t('Gateway Serial') || 'Serial Gateway']: gw.gatewaySerial || 'N/A',
                [t('Online Camera Count') || 'Số Camera Online']: gw.cameraOnline ?? 0,
                [t('Gap Duration') || 'Thời gian thiếu']: formatSecondsToHHMMSS(gw.totalGapS || 0),
                [t('Upload Rate (%)') || 'Tỉ lệ Upload (%)']: gw.uploadedPercent,
                [t('Recoverable Duration') || 'Recoverable duration']: formatSecondsToHHMMSS(gw.totalUploadS || 0),
                [t('Recoverable Rate (%)') || 'Recoverable Rate (%)']: gw.uploadablePercent ?? 0,
                [t('Unrecoverable Duration') || 'Unrecoverable duration']: formatSecondsToHHMMSS(gw.unrecoverableDurationS || 0),
                [t('Unrecoverable Rate (%)') || 'Unrecoverable Rate (%)']: gw.unrecoverableRatePercent ?? 0,
            }));

            const worksheet = XLSX.utils.json_to_sheet(exportData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Gateway SLA');

            const filename = `report_gateway_sla_${namespace}_${fromDateStr}_to_${toDateStr}.xlsx`;
            XLSX.writeFile(workbook, filename);
            message.success(t('Export Excel successfully') || 'Export Excel successfully');
        } catch (err) {
            console.error('Error exporting Excel:', err);
            message.error(t('Export Excel failed') || 'Export Excel failed');
        }
    }, [allCalculatedGateways, namespace, fromDateStr, toDateStr, t]);

    const chartHeight = Math.max(360, top20LowestGateways.length * 30 + 40);

    if (namespace !== "hdbank.com.vn") return null;

    return (
        <div
            className="chart-item"
            style={{
                width: '100%',
                marginTop: '16px',
                backgroundColor: '#141414',
                borderRadius: '8px',
                padding: '16px 20px',
                border: '1px solid #262626',
            }}
        >
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                    flexWrap: 'wrap',
                    gap: '12px',
                    width: '100%',
                }}
            >
                <Text style={{ fontSize: '16px', fontWeight: 'bold', color: '#fff' }}>
                    {t('TOP 20 GATEWAYS WITH LOWEST UPLOAD RATE')}
                </Text>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <AntTooltip title={t('Reload')}>
                        <ReloadOutlined
                            onClick={fetchGatewayGaps}
                            style={{
                                color: '#8c8c8c',
                                fontSize: '16px',
                                cursor: 'pointer',
                            }}
                        />
                    </AntTooltip>
                    <div className="input_date_picker">
                        <Select
                            style={{ width: 100 }}
                            value={getSelectedOption()}
                            onChange={(value) => value && changeDateRanger(value)}
                        >
                            <Option value={1}>{t('Today')}</Option>
                            <Option value={3}>{t('3 days')}</Option>
                            <Option value={7}>{t('7 days')}</Option>
                            <Option value={30}>{t('30 days')}</Option>
                        </Select>
                        <RangePicker
                            style={{ width: 230 }}
                            value={
                                dateRanger && dateRanger.length === 2
                                    ? [moment(dateRanger[0]), moment(dateRanger[1])]
                                    : []
                            }
                            onChange={handleDateRangerChange}
                            disabledDate={(current) => current && current.isAfter(moment(), 'day')}
                        />
                    </div>
                    <Button
                        onClick={handleExportExcel}
                        style={{
                            borderColor: '#0e9347',
                            color: '#0e9347',
                            backgroundColor: 'transparent',
                            fontWeight: '500',
                            fontSize: '13px',
                            height: '28px',
                            padding: '0 12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                        }}
                    >
                        {t('Export') || 'Export'}
                    </Button>
                </div>
            </div>

            {loading ? (
                <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Spin size="default" />
                </div>
            ) : top20LowestGateways.length === 0 ? (
                <Empty description={t('No Gateway Data')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
                <ResponsiveContainer width="100%" height={chartHeight}>
                    <BarChart
                        layout="vertical"
                        data={top20LowestGateways}
                        margin={{ top: 10, right: 50, left: 10, bottom: 10 }}
                    >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#262626" />
                        <XAxis
                            type="number"
                            domain={[0, 100]}
                            tick={{ fill: '#8c8c8c', fontSize: 12 }}
                            tickFormatter={(val) => `${val}%`}
                            stroke="#434343"
                        />
                        <YAxis
                            type="category"
                            dataKey="displayName"
                            width={260}
                            tick={{ fill: '#d9d9d9', fontSize: 12 }}
                            stroke="#434343"
                        />
                        <Bar
                            dataKey="uploadedPercent"
                            barSize={16}
                            radius={[0, 4, 4, 0]}
                            shape={<CustomBarShape t={t} />}
                        >
                            {top20LowestGateways.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={getBarColor(entry.uploadedPercent)} />
                            ))}
                            <LabelList dataKey="uploadedPercent" content={<BarLabel />} />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    );
};

export default GatewaySLA;
